import {
  EAppType,
  EUserType,
  IDadosDispositivo,
  IUser,
  TPayloadNotification,
} from "@deltasge/marauders-map";
import { FirebaseError } from "firebase/app";
import {
  getToken,
  onMessage as onMessageFBM,
  isSupported as isSupportedFBM,
  Messaging,
} from "firebase/messaging";
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios, { AxiosResponse } from "axios";

import {
  NotificationTypes,
  PushNotificationService,
  WindowWebKitIOS,
} from "interfaces";

import { api, Environment, history } from "configs";
import { showSnack, snack } from "components/GlobalSnackbar";
import { NotificationComponent } from "components/NotificationComponent";
import { useConfirmDialog } from "hooks/useDialogConfirm";
import { useAppDispatch, useAppSelector } from "store";
import { set as setUsuario } from "store/modules/auth/usuario";
import { getError, getUrlFromNotification, isWebKit } from "utils";
import { useLocalStorage } from "react-use";

export const PushNotificationContext = createContext<PushNotificationService>({
  isSupported: false,
  permission: "default",
  requestPermission: () => {},
  requestPushToken: () => {},
  onChangeDipositivo: async () => {},
  loadingDispositivo: false,
});

export const usePushNotification = () => {
  return useContext(PushNotificationContext);
};

const getOrRegisterServiceWorker = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return undefined;
  }

  // 1. Obtém todos os registros de Service Worker atuais para limpar duplicatas
  const existingRegistrations =
    await navigator.serviceWorker.getRegistrations();

  // Verifica se o service-worker.js principal (PWA) está presente
  const mainSwReg = existingRegistrations.find(
    (reg) =>
      reg.active?.scriptURL.includes("service-worker.js") ||
      reg.installing?.scriptURL.includes("service-worker.js") ||
      reg.waiting?.scriptURL.includes("service-worker.js")
  );

  // Se o service-worker.js principal existe, remove qualquer firebase-messaging-sw.js legado duplicado
  if (mainSwReg) {
    const duplicateSw = existingRegistrations.filter(
      (reg) =>
        reg !== mainSwReg &&
        (reg.active?.scriptURL.includes("firebase-messaging-sw.js") ||
          reg.installing?.scriptURL.includes("firebase-messaging-sw.js") ||
          reg.waiting?.scriptURL.includes("firebase-messaging-sw.js"))
    );
    for (const dup of duplicateSw) {
      dup.unregister().catch(() => {});
    }

    if (mainSwReg.active) return mainSwReg;
    return await navigator.serviceWorker.ready;
  }

  // 2. Se já existe um único registro ativo, remove eventuais sobras e usa ele
  if (existingRegistrations.length > 0) {
    const primaryReg = existingRegistrations[0];
    if (existingRegistrations.length > 1) {
      for (let i = 1; i < existingRegistrations.length; i++) {
        existingRegistrations[i].unregister().catch(() => {});
      }
    }
    if (primaryReg.active) return primaryReg;
    return await navigator.serviceWorker.ready;
  }

  // 3. Caso nenhum service worker esteja registrado:
  // Em produção usamos o service-worker.js unificado. Em dev mode, usamos firebase-messaging-sw.js
  const swUrl =
    process.env.NODE_ENV === "production"
      ? `${process.env.PUBLIC_URL || ""}/service-worker.js`
      : `${process.env.PUBLIC_URL || ""}/firebase-messaging-sw.js`;

  const registration = await navigator.serviceWorker.register(swUrl);
  if (registration.active) return registration;

  const sw = registration.installing || registration.waiting;
  if (sw) {
    await new Promise<void>((resolve) => {
      const stateListener = (e: any) => {
        if (e.target.state === "activated") {
          sw.removeEventListener("statechange", stateListener);
          resolve();
        }
      };
      sw.addEventListener("statechange", stateListener);
    });
  }

  return await navigator.serviceWorker.ready;
};

export const ProviderPushNotification: FC<
  PropsWithChildren<{
    fcm: Messaging;
  }>
> = ({ children, fcm }) => {
  const {
    usuario: { usuario },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();

  const [dispositivo, setDispositivo] = useState<IDadosDispositivo>();
  const [isSupported, setIsSupported] = useState<boolean>(false);

  // Inicializa a permissão lendo o estado real do navegador se for Web,
  // ou do localStorage se for WebKit (iOS)
  const [permission, setPermissionState] = useState<NotificationTypes>(() => {
    if (
      !isWebKit &&
      typeof window !== "undefined" &&
      "Notification" in window
    ) {
      return Notification.permission;
    }
    const stored = localStorage.getItem(
      "notification-permission"
    ) as NotificationTypes;
    return stored || "default";
  });

  const setPermission = useCallback((newPermission: NotificationTypes) => {
    setPermissionState(newPermission);
    try {
      localStorage.setItem("notification-permission", newPermission);
    } catch (e) {
      // ignore
    }
  }, []);

  const [loadingDispositivo, setLoadingDispositivo] = useState(false);

  const [token, setToken] = useLocalStorage<string | undefined>(
    "notification-token",
    undefined
  );

  const [promptDismissed, setPromptDismissed] = useLocalStorage<boolean>(
    "notification-prompt-dismissed",
    false
  );

  const [lastNotification, setLastNotification] = useLocalStorage<any>(
    "notification-last-notification",
    undefined
  );

  const registeringRef = useRef(false);
  const lastRegisteredKeyRef = useRef<string | null>(null);
  const isGettingTokenRef = useRef(false);

  // Reset do token registrado quando o usuário deslogar
  useEffect(() => {
    if (!usuario) {
      lastRegisteredKeyRef.current = null;
      registeringRef.current = false;
    }
  }, [usuario]);

  // Função para sincronizar a permissão real do navegador
  const syncBrowserPermission = useCallback(() => {
    if (isWebKit) return;
    if (typeof window !== "undefined" && "Notification" in window) {
      const current = Notification.permission;
      setPermissionState((prev) => {
        if (prev !== current) {
          try {
            localStorage.setItem("notification-permission", current);
          } catch (e) {
            // ignore
          }
          return current;
        }
        return prev;
      });
    }
  }, []);

  // Observa mudanças de permissão no navegador (Permissions API + eventos de foco/visibilidade)
  useEffect(() => {
    if (isWebKit) return;

    syncBrowserPermission();

    const handleFocus = () => syncBrowserPermission();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncBrowserPermission();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    let permStatus: PermissionStatus | null = null;
    if (
      typeof navigator !== "undefined" &&
      "permissions" in navigator &&
      navigator.permissions.query
    ) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((status) => {
          permStatus = status;
          const onChange = () => {
            const state = status.state;
            const mapped: NotificationTypes =
              state === "prompt" ? "default" : state;
            setPermissionState((prev) => {
              if (prev !== mapped) {
                try {
                  localStorage.setItem("notification-permission", mapped);
                } catch (e) {
                  // ignore
                }
                return mapped;
              }
              return prev;
            });
          };
          permStatus.addEventListener("change", onChange);
        })
        .catch(() => {
          // Navegadores sem suporte a query 'notifications' caem no focus/visibilitychange
        });
    }

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (permStatus) {
        permStatus.onchange = null;
      }
    };
  }, [syncBrowserPermission]);

  // Se a permissão não for mais granted e existir token localmente, limpa o token local
  useEffect(() => {
    if (permission !== "granted" && token) {
      setToken(undefined);
      setDispositivo(undefined);
      lastRegisteredKeyRef.current = null;
    }
  }, [permission, token, setToken]);

  const registrarPushUsuario = useCallback(
    async (user: IUser, pushToken: string) => {
      try {
        if (user.tipoUsuario === EUserType.UsuarioInterno) {
          return;
        }

        if (!user.id) {
          return;
        }

        const registrationKey = `${user.id}-${pushToken}`;
        // Trava para evitar loops ou execuções simultâneas
        if (
          lastRegisteredKeyRef.current === registrationKey ||
          registeringRef.current
        ) {
          return;
        }

        registeringRef.current = true;
        lastRegisteredKeyRef.current = registrationKey;

        const codigoZeroEsqueda = parseInt(user.escolaId, 10)
          .toString()
          .padStart(4, "0");

        const payload: Partial<IDadosDispositivo> = {
          idUsuario: user.id,
          app: EAppType.DeltaSGE,
        };

        const { data } = await axios.put<IDadosDispositivo>(
          `${Environment.API_URL}/api/${codigoZeroEsqueda}/dados-dispositivo/tipo-usuario/${user.tipoUsuario}/token/${pushToken}`,
          payload,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );
        setDispositivo(data);

        const currentTokens = Array.isArray(user.pushTokens)
          ? user.pushTokens
          : [];
        if (!currentTokens.includes(pushToken)) {
          const pushTokens = currentTokens.concat(pushToken);
          const { data: updatedUser }: AxiosResponse<IUser> = await axios.put(
            `${Environment.CONFIG_DELTA_API_URL}/usuarios/usuario/${user.deltaId}`,
            {
              ...user,
              pushTokens,
            },
            {
              headers: { Authorization: `Bearer ${user.token}` },
            }
          );
          dispatch(setUsuario({ ...updatedUser, pushTokens }));
        }
      } catch (error) {
        lastRegisteredKeyRef.current = null;
        snack.error(getError(error));
      } finally {
        registeringRef.current = false;
      }
    },
    [dispatch]
  );

  const setPermissionFromWebKit = useCallback(
    (evt: Event) => {
      try {
        const state = (evt as CustomEvent).detail;
        if (typeof state !== "string") {
          setPermission("denied");
          return;
        }
        if (
          ["authorized", "ephemeral", "provisional", "granted"].includes(state)
        ) {
          setPermission("granted");
        } else if (["unknown", "notDetermined"].includes(state)) {
          setPermission("default");
        } else {
          setPermission("denied");
        }
      } catch (error) {
        setPermission("denied");
      }
    },
    [setPermission]
  );

  const requestPushTokenFromWebKit = async () => {
    await (
      window as WindowWebKitIOS
    ).webkit.messageHandlers.getPushToken.postMessage("getPushToken");
  };

  const requestPushTokenFromWeb = useCallback(async () => {
    if (isGettingTokenRef.current) return;
    isGettingTokenRef.current = true;

    try {
      let swRegistration: ServiceWorkerRegistration | undefined;
      try {
        swRegistration = await getOrRegisterServiceWorker();
      } catch (swError) {
        // eslint-disable-next-line no-console
        console.warn(
          "Não foi possível registrar o service worker antecipadamente:",
          swError
        );
      }

      const currentToken = await getToken(fcm, {
        vapidKey: Environment.FCM_VAPIDKEY,
        serviceWorkerRegistration: swRegistration,
      });

      if (currentToken) {
        setToken(currentToken);
      }
    } catch (error) {
      if (
        !(error instanceof FirebaseError) ||
        error.code !== "messaging/permission-blocked"
      ) {
        // eslint-disable-next-line no-console
        console.error("Erro ao obter token FCM:", error);
      }
    } finally {
      isGettingTokenRef.current = false;
    }
  }, [fcm, setToken]);

  const requestPushToken = useCallback(() => {
    try {
      if (isWebKit) {
        requestPushTokenFromWebKit();
      } else {
        requestPushTokenFromWeb().catch((err) => {
          // eslint-disable-next-line no-console
          console.error("Erro em requestPushTokenFromWeb:", err);
        });
      }
    } catch (error) {
      snack.error(getError(error));
    }
  }, [requestPushTokenFromWeb]);

  const requestPermissionFromWeb = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      // Não chamamos requestPushTokenFromWeb aqui diretamente
      // para evitar chamada duplicada em paralelo. O useEffect
      // (permission === "granted" && !token) disparará a busca de token.
    } catch (error) {
      snack.error(getError(error));
    }
  }, [setPermission]);

  const requestPermissionFromWebKit = async () => {
    await (
      window as WindowWebKitIOS
    ).webkit.messageHandlers.pushPermissionRequest.postMessage(
      "pushPermissionRequest"
    );
  };

  const requestPermission = useCallback(() => {
    try {
      if (isWebKit) {
        requestPermissionFromWebKit();
      } else {
        requestPermissionFromWeb();
      }
    } catch (error) {
      snack.error(getError(error));
    }
  }, [requestPermissionFromWeb]);

  const setTokenFromWebKit = useCallback(
    (evt: Event) => {
      const receivedToken = (evt as CustomEvent).detail;
      if (typeof receivedToken === "string") setToken(receivedToken);
    },
    [setToken]
  );

  const requestSupport = useCallback(async () => {
    try {
      if (isWebKit) {
        setIsSupported(
          (window as WindowWebKitIOS).webkit?.messageHandlers != undefined
        );
      } else {
        const supported = await isSupportedFBM();
        setIsSupported(supported);
      }
    } catch (error) {
      setIsSupported(false);
      snack.error(getError(error));
    }
  }, []);

  const onMessageFromWebKit = useCallback(
    (evt: Event) => {
      try {
        const payloadStr = (evt as CustomEvent).detail as string;
        let payload: TPayloadNotification;
        try {
          payload = JSON.parse(payloadStr) as TPayloadNotification;
        } catch (error) {
          snack.error("Erro ao converter payload");
          return;
        }
        setLastNotification(payload);
        if (
          typeof payload.titulo === "string" &&
          typeof payload.descricao === "string"
        ) {
          const url = getUrlFromNotification(payload);
          if (url) {
            if (url.startsWith("http")) {
              window.open(url, "_blank", "noopener,noreferrer");
            } else {
              history.push(url);
            }
          }
        }
      } catch (error) {
        snack.error(getError(error));
      }
    },
    [setLastNotification]
  );

  // Escuta mensagens FCM em foreground com limpeza adequada (evita vazamento de memória)
  useEffect(() => {
    if (permission === "granted" && !isWebKit && fcm) {
      const unsubscribe = onMessageFBM(fcm, (payload) => {
        setLastNotification(payload);
        if (payload && payload.data) {
          const data = payload.data as TPayloadNotification;
          showSnack(
            <NotificationComponent
              payload={data}
              notification={{
                body: data.descricao,
                icon: data.icon || undefined,
                image: data.image || undefined,
                title: data.titulo,
              }}
            />
          );
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [permission, fcm, setLastNotification]);

  const onChangeDipositivo = useCallback(
    async (payload: Partial<IDadosDispositivo>) => {
      try {
        if (!token || permission !== "granted" || !dispositivo) return;

        setLoadingDispositivo(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _, ...body1 } = dispositivo;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: __, ...body2 } = payload;

        await api.put<IDadosDispositivo>(
          `dados-dispositivo/tipo-usuario/${usuario?.tipoUsuario}/token/${token}`,
          {
            ...body1,
            ...body2,
            app: EAppType.DeltaSGE,
          }
        );

        setDispositivo({
          ...dispositivo,
          ...payload,
        });
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoadingDispositivo(false);
      }
    },
    [dispositivo, token, permission, usuario?.tipoUsuario]
  );

  const { show, RenderDialog } = useConfirmDialog<boolean>({
    defaults: {
      title: "Notificações",
      content: "Deseja receber notificações?",
    },
    onConfirmed: (payload, hide) => {
      hide();
      setPromptDismissed(true);
      requestPermission();
    },
    onCanceled: (hide) => {
      setPromptDismissed(true);
      hide();
    },
  });

  useEffect(() => {
    requestSupport();
    window.addEventListener("pushPermissionRequest", setPermissionFromWebKit);
    window.addEventListener("pushPermissionState", setPermissionFromWebKit);
    window.addEventListener("pushToken", setTokenFromWebKit);
    window.addEventListener("pushNotification", onMessageFromWebKit);

    return () => {
      window.removeEventListener(
        "pushPermissionRequest",
        setPermissionFromWebKit
      );
      window.removeEventListener(
        "pushPermissionState",
        setPermissionFromWebKit
      );
      window.removeEventListener("pushToken", setTokenFromWebKit);
      window.removeEventListener("pushNotification", onMessageFromWebKit);
    };
  }, [
    onMessageFromWebKit,
    requestSupport,
    setPermissionFromWebKit,
    setTokenFromWebKit,
  ]);

  // Exibe o diálogo inicial apenas se nunca foi dispensado e a permissão for default
  useEffect(() => {
    if (
      usuario &&
      isSupported &&
      permission === "default" &&
      !promptDismissed
    ) {
      show(true);
    }
  }, [isSupported, usuario, permission, promptDismissed, show]);

  // Se a permissão estiver concedida mas não tivermos o token, solicita
  useEffect(() => {
    if (permission === "granted" && !token && isSupported) {
      requestPushToken();
    }
  }, [permission, requestPushToken, token, isSupported]);

  // Registra o token com o usuário autenticado
  useEffect(() => {
    if (permission === "granted" && token && usuario) {
      registrarPushUsuario(usuario, token);
    }
  }, [token, permission, usuario, registrarPushUsuario]);

  return (
    <PushNotificationContext.Provider
      value={{
        isSupported: isSupported || false,
        permission: permission || "default",
        requestPermission,
        requestPushToken,
        onChangeDipositivo,
        token,
        lastNotification,
        dispositivo,
        loadingDispositivo,
      }}
    >
      <RenderDialog />
      {children}
    </PushNotificationContext.Provider>
  );
};
